import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SERVICES } from "../data/content";
import { useReveal } from "../hooks/useReveal";

const CATEGORIES = [
    {
        title: "Residential Interiors",
        blurb:
            "Apartments, villas and independent homes — designed around how your family actually lives.",
        image: "/generated/quiet-apartment-living.jpg",
        items: [
            "3, 4 & 5 BHK Apartments",
            "Villas & Farmhouses",
            "Duplexes & Penthouses",
            "Turnkey Delivery",
            "Modular Kitchens",
            "Wardrobes & Joinery",
        ],
    },
    {
        title: "Commercial Interiors",
        blurb:
            "Boutique offices, studios and hospitality spaces — built with the same craft as our homes.",
        image: "/generated/home-office.jpg",
        items: [
            "Founder & Creative Studios",
            "Boutique Offices",
            "Retail & Showroom",
            "F&B Interiors",
            "Lighting Design",
            "3D Visualization",
        ],
    },
    {
        title: "Landscaping",
        blurb:
            "Balconies, terraces and courtyards — designed for slow evenings and long conversations.",
        image: "/generated/villa-exterior.jpg",
        items: [
            "Terrace Gardens",
            "Balcony Landscapes",
            "Courtyards",
            "Vertical Gardens",
            "Outdoor Lighting",
            "Furniture & Planters",
        ],
    },
];

const Services = () => {
    const ref = useReveal();
    return (
        <div ref={ref} data-testid="services-page" className="pt-24 md:pt-32">
            {/* Header */}
            <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
                    <div className="lg:col-span-8 reveal">
                        <p className="eyebrow mb-6">The Studio Practice</p>
                        <h1
                            className="font-display text-samora-ink leading-[0.95] tracking-tight"
                            style={{ fontSize: "clamp(2.75rem, 7vw, 7rem)" }}
                        >
                            Services,<br />
                            <span className="italic font-serif text-samora-gold">
                                drawn end to end.
                            </span>
                        </h1>
                    </div>
                    <p className="lg:col-span-4 text-samora-text/75 text-lg leading-relaxed reveal">
                        We offer the full arc of a home — from the first
                        drawing to the last light switch — with the same crew,
                        the same designer, the same standard.
                    </p>
                </div>
            </section>

            {/* Category blocks */}
            {CATEGORIES.map((c, idx) => (
                <section
                    key={c.title}
                    data-testid={`services-category-${idx}`}
                    className={`py-24 md:py-32 ${
                        idx % 2 === 1 ? "bg-samora-sand/40" : "bg-samora-bone"
                    }`}
                >
                    <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                        <div
                            className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center ${
                                idx % 2 === 1 ? "lg:flex-row-reverse" : ""
                            }`}
                        >
                            <div
                                className={`lg:col-span-6 reveal ${
                                    idx % 2 === 1 ? "lg:order-2" : ""
                                }`}
                            >
                                <img
                                    src={c.image}
                                    alt={c.title}
                                    className="w-full h-[500px] md:h-[640px] object-cover"
                                    loading="lazy"
                                />
                            </div>
                            <div
                                className={`lg:col-span-6 reveal ${
                                    idx % 2 === 1 ? "lg:order-1" : ""
                                }`}
                            >
                                <p className="eyebrow mb-4">
                                    {String(idx + 1).padStart(2, "0")}
                                </p>
                                <h2
                                    className="font-display text-samora-ink leading-[1] tracking-tight"
                                    style={{
                                        fontSize: "clamp(2rem, 5vw, 4.5rem)",
                                    }}
                                >
                                    {c.title}
                                </h2>
                                <p className="mt-6 text-samora-text/75 text-base md:text-lg leading-relaxed max-w-lg">
                                    {c.blurb}
                                </p>
                                <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                                    {c.items.map((i) => (
                                        <li
                                            key={i}
                                            className="text-samora-ink border-b border-samora-ink/15 py-3 text-sm md:text-base flex items-center justify-between"
                                        >
                                            {i}{" "}
                                            <span className="text-samora-gold text-xs">
                                                ·
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/contact"
                                    data-testid={`services-cta-${idx}`}
                                    className="btn-samora mt-10"
                                >
                                    Enquire <ArrowRight size={16} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            ))}

            {/* Full services grid */}
            <section className="py-24 md:py-32 bg-samora-ink text-samora-bone">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <div className="max-w-3xl mb-16 md:mb-24 reveal">
                        <p className="eyebrow text-samora-gold mb-6">
                            Every discipline in-house
                        </p>
                        <h2
                            className="font-display leading-[0.95] tracking-tight"
                            style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)" }}
                        >
                            The full scope,<br />
                            <span className="italic font-serif text-samora-gold">
                                one studio.
                            </span>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-10 gap-x-12">
                        {SERVICES.map((s, i) => (
                            <div
                                key={s.title}
                                className="reveal border-t border-samora-bone/20 pt-6"
                            >
                                <span className="font-serif text-samora-gold text-lg">
                                    — {String(i + 1).padStart(2, "0")}
                                </span>
                                <h3 className="font-display text-2xl md:text-3xl mt-2">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-samora-bone/70 text-sm leading-relaxed max-w-sm">
                                    {s.blurb}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Services;
